import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft } from "lucide-react";

const bearingsList = [
  "Ball Bearings",
  "Roller Bearings",
  "Bearings for Komatsu®",
  "Bearings for John Deere®",
  "Copper Bonded Bearings",
  "Cylindrical Roller Bearings",
  "Dimpled & Milling Bearings",
  "Inner & Outer Races",
  "Tapered Roller Bearings",
  "Spherical Bearings",
  "Sleeve Bearings",
  "Needle Bearings",
];

const bearingTypes = [
  {
    id: "ball-bearings",
    title: "Ball Bearings:",
    image: "https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/ymmebz18_Bearings%20.jpg",
    description: "Most widely used bearings for high precision applications such as aerospace, agriculture, construction and industrial. Ball bearings consist of an inner and outer ring with a cage containing precision balls. We carry Radial Ball bearings and Angular Contact Ball bearings."
  },
  {
    id: "tapered-bearings",
    title: "Tapered Roller Bearings:",
    image: "https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/uldu94j5_Tapered%20Roller%20Bearings-%20.jpg",
    description: "Consist of 4 interdependent components: the cone, or inner ring; the cup, or outer ring; the tapered rollers; and the cage, or roller retainer. The taper angles allow the bearing to handle a combination of radial and thrust loads. The greater the cup angle, the greater its thrust load handling capacity."
  },
  {
    id: "spherical-bearings",
    title: "Spherical Roller Bearings:",
    image: "https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/uniojuwf_Spherical%20Roller%20Bearings-%20.jpg",
    description: "Designed for extremely severe applications that must support high loads, contaminated environments, shocks, and vibrations. We carry Stamped Steel Cage and Machined Brass Cage designs to suit your applications. These bearings feature the industry's highest load rating: a full 18% higher than a standard spherical roller bearing offering a 75% extension of service life."
  },
  {
    id: "cylindrical-bearings",
    title: "Cylindrical Roller Bearings:",
    image: "https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/wzqfb3me_Cylindrical%20Roller%20Bearings-%20.jpg",
    description: "Cylindrical roller bearings have rollers that provide a modified line contact with the cylindrical inner and outer ring raceways, while the rollers are guided by ground ribs. The cylindrical shape allows the inner ring to have axial movement relative to the outer ring. This allows thermal expansion, where both rings must be press-fitted."
  }
];

export const BearingsCategory = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-white py-12 md:py-16 overflow-hidden" data-testid="bearings-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
          >
            {/* Left - Content */}
            <div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8">
                Bearings
              </h1>
              <p className="text-gray-600 text-lg leading-loose max-w-xl">
                MAK Group has been a trusted distributor for high-quality bearings for over 15 years. We have a wide range of bearings available in stock. Most tapered, cylindrical and spherical roller bearings feature case-carburized components. Rings and rollers are made from case-hardened alloy steel of "Bearing Quality" to provide superior fatigue life and reliability. No matter what you are looking for, MAK has you covered with the proper fit for your application.
              </p>
            </div>
            
            {/* Right - Large Image */}
            <div className="flex justify-end">
              <img 
                src="https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/4ajkb965_Bearings-Komatsu.png" 
                alt="Bearings" 
                className="w-full max-w-lg h-auto object-contain"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Bearings List */}
            <div>
              {/* Bearing Image */}
              <div className="mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/hudvhauv_Bearingsxx.jpg" 
                  alt="MAK Bearings" 
                  className="w-full max-w-md h-auto"
                />
              </div>

              {/* List Header */}
              <h2 className="font-heading text-xl font-bold text-gray-900 mb-4">
                The following are some of the MAK Bearings replacement parts:
              </h2>

              {/* Bearings List */}
              <ul className="space-y-2 mb-6">
                {bearingsList.map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="text-[#F5B800]">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="font-bold text-gray-900">& More</p>
            </div>

            {/* Right Column - Bearing Type Details */}
            <div className="space-y-10">
              {bearingTypes.map((type, index) => (
                <motion.div
                  key={type.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col sm:flex-row gap-6"
                >
                  <div className="w-full sm:w-48 h-48 bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
                    <img
                      src={type.image}
                      alt={type.title}
                      className="w-full h-full object-contain p-4"
                    />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">
                      {type.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {type.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Additional Info */}
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900 mb-6 border-b-2 border-gray-200 pb-2">
                ADDITIONAL INFORMATION
              </h3>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-4 shadow-sm">
                  <h4 className="font-bold text-gray-900 mb-2">Hydraulic Pumps</h4>
                  <p className="text-sm text-gray-600">
                    MAK Hydraulic pumps are rugged, reliable and interchangeable with many popular brands.
                  </p>
                </div>
                <div className="bg-white p-4 shadow-sm">
                  <h4 className="font-bold text-gray-900 mb-2">Piston Pumps</h4>
                  <p className="text-sm text-gray-600">
                    Hydraulic piston pumps are classified as fixed displacement or variable displacement based on the piston position.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Box */}
            <div className="bg-[#F5B800] p-8">
              <h3 className="font-heading text-xl font-bold text-black mb-4">
                Product Inquiry
              </h3>
              <p className="text-black/80 mb-6">
                Need more information about our bearings? Contact us for pricing and availability.
              </p>
              <a
                href="https://wa.me/971563419000"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-gray-900 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contact Us on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Back Link */}
      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
          <Link
            to="/online-store"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#D4A000] transition-colors"
          >
            <ArrowLeft size={18} />
            Back to All Categories
          </Link>
        </div>
      </section>
    </div>
  );
};
